import PropTypes from 'prop-types';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { batch } from 'react-redux';

// material-ui
import { TextField, Box } from '@mui/material';

// third party
import { useIntl } from 'react-intl';

// project imports
import { useSelector, useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { ApiWorkOrdersGetCollection } from 'store/app/workmgmt/workorder';
import Autocomplete from 'ui-component/extended/Autocomplete';

// ==============================|| SELECT WORK MGMT WORK ORDER FIELD ||============================== //

export const renderWorkOrder = (workOrder) => {
    if (!workOrder) return '';
    if (typeof workOrder === 'string') return workOrder;
    return 'code' in (workOrder || {}) && 'name' in (workOrder || {}) ? `[${workOrder.code}] ${workOrder.name}` : '';
};

const WorkOrderSelect = ({
    value,
    onChange = () => {},
    validationErrors = null,
    baseId = '',
    enableSearch = true,
    loadData = false,
    baseFilterGroups = [],
    ...others
}) => {
    const dispatch = useDispatch();
    const intl = useIntl();

    const { list: workOrderList, quickSearchFilterDef: workOrderQuickSearchFilterDef } = useSelector((state) => state.workOrder);

    const [isLoadingWorkOrder, setIsLoadingWorkOrder] = useState(false);
    const [userInputWorkOrder, setUserInputWorkOrder] = useState(renderWorkOrder(value));
    const [workOrders, setWorkOrders] = useState(workOrderList || []);

    const workOrderFilterRows = useMemo(
        () =>
            workOrderQuickSearchFilterDef?.fields?.map((fd) => ({
                field: fd,
                operator: 'like',
                value: userInputWorkOrder
            })),
        [workOrderQuickSearchFilterDef, userInputWorkOrder]
    );

    const handleLoadWorkOrder = useCallback(() => {
        setIsLoadingWorkOrder(true);
        ApiWorkOrdersGetCollection({
            filters: baseFilterGroups,
            successCallback: (response) =>
                batch(() => {
                    setIsLoadingWorkOrder(false);
                    setWorkOrders(response);
                    setUserInputWorkOrder('');
                }),
            errorCallback: (error) =>
                batch(() => {
                    setIsLoadingWorkOrder(false);
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: error || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                            variant: 'alert',
                            alert: { color: 'error' }
                        })
                    );
                }),
            updateStore: false
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseFilterGroups]);

    useEffect(() => {
        if (loadData) {
            handleLoadWorkOrder();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadData]);

    useEffect(() => {
        if (enableSearch && userInputWorkOrder?.length >= 3) {
            setIsLoadingWorkOrder(true);
            ApiWorkOrdersGetCollection({
                filters: [...baseFilterGroups, { filterType: 'or', filterRows: workOrderFilterRows }],
                successCallback: (response) =>
                    batch(() => {
                        setIsLoadingWorkOrder(false);
                        setWorkOrders(response);
                    }),
                errorCallback: (error) =>
                    batch(() => {
                        setIsLoadingWorkOrder(false);
                        dispatch(
                            openSnackbar({
                                open: true,
                                message: error || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                                variant: 'alert',
                                alert: { color: 'error' }
                            })
                        );
                    }),
                updateStore: false
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enableSearch, workOrderFilterRows]);

    useEffect(() => {
        if (value === null) {
            setUserInputWorkOrder('');
            handleLoadWorkOrder();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
        <Autocomplete
            id={`${baseId}-workOrder`}
            value={value}
            onChange={(value) =>
                batch(() => {
                    onChange(value);
                    setUserInputWorkOrder(renderWorkOrder(value));
                })
            }
            inputValue={userInputWorkOrder || renderWorkOrder(value)}
            onInputChange={(value, reason) => {
                if (reason === 'input') {
                    setUserInputWorkOrder(value);
                }
                if (reason === 'clear') {
                    setUserInputWorkOrder('');
                    handleLoadWorkOrder();
                }
            }}
            loading={isLoadingWorkOrder}
            options={workOrders}
            freeSolo={enableSearch}
            getOptionLabel={(option) => renderWorkOrder(option)}
            renderOption={(props, option) => (
                <Box component="li" {...props}>
                    {renderWorkOrder(option)}
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    inputProps={{ ...params.inputProps }}
                    error={Boolean(validationErrors)}
                    helperText={validationErrors}
                />
            )}
            {...others}
        />
    );
};

WorkOrderSelect.propTypes = {
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
    onChange: PropTypes.func,
    validationErrors: PropTypes.any,
    baseId: PropTypes.string,
    enableSearch: PropTypes.bool,
    loadData: PropTypes.bool,
    baseFilterGroups: PropTypes.array
};

export default WorkOrderSelect;
